/**
 * 蓝牙打印服务 - 支持 Web Bluetooth 和原生蓝牙（Capacitor App）
 * 
 * 兼容性：
 * - ✅ App 环境：使用原生蓝牙 API，自动连接，不弹框
 * - ✅ Chrome 104+：使用 getDevices API，自动连接
 * - ✅ Chrome/Edge：使用 requestDevice API，需要用户点击
 * 
 * 功能：
 * - 自动重连机制
 * - 设备信息持久化（数据库）
 * - App 环境下完全自动，无需用户操作
 */

import GBK from '@/utils/gbk.min.js'
import { getBluetoothDevices, saveBluetoothDevice } from '@/api/bluetooth-printer'

class BluetoothPrinter {
  constructor() {
    this.device = null
    this.server = null
    this.service = null
    this.characteristic = null
    this.isConnected = false
    
    // 原生蓝牙（App 环境）
    this.nativeDevice = null
    this.isNativeMode = false
    
    // 缓存的设备信息（从数据库加载）
    this.savedDeviceId = null
    this.savedDeviceName = null
    
    // 重连配置
    this.maxRetries = 3
    this.retryDelay = 1000 // 毫秒
    
    // 长连接和自动重连
    this.autoReconnectEnabled = true
    this.isReconnecting = false
    this.reconnectTimer = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    
    // 当前用户 ID（从页面获取）
    this.userId = null
    
    // 打印机服务 UUID (常见蓝牙打印机)
    this.SERVICE_UUIDS = [
      '000018f0-0000-1000-8000-00805f9b34fb',
      '49535343-fe7d-4ae5-8fa9-9fafd205e455',
    ]
    
    // BLE 分包大小（IZM 打印机每次最多 100 字节）
    this.BLE_PACK_SIZE = 100
    
    // BLE 写入特征缓存
    this.bleWriteCharacteristic = null
    this.bleServiceUuid = null
  }

  // ==================== 平台检测 ====================

  isAppEnvironment() {
    return typeof window !== 'undefined' && window.Capacitor
  }

  isIOS() {
    if (!this.isAppEnvironment()) return false
    return (window.Capacitor?.getPlatform?.() || '') === 'ios'
  }

  isAndroid() {
    if (!this.isAppEnvironment()) return false
    return (window.Capacitor?.getPlatform?.() || '') === 'android'
  }

  // ==================== 用户与设备管理 ====================

  setUserId(userId) {
    this.userId = userId
  }

  async loadSavedDevice() {
    try {
      const res = await getBluetoothDevices(this.userId)
      if (res?.success && res.data?.length > 0) {
        const defaultDevice = res.data.find(d => d.isDefault) || res.data[0]
        this.savedDeviceId = defaultDevice.deviceId
        this.savedDeviceName = defaultDevice.deviceName
        return true
      }
    } catch (error) {
      console.warn('加载蓝牙设备列表失败:', error)
    }
    return false
  }

  async saveDeviceToDatabase(deviceId, deviceName) {
    try {
      const res = await saveBluetoothDevice(deviceId, deviceName, this.userId)
      if (res?.success) {
        this.savedDeviceId = deviceId
        this.savedDeviceName = deviceName
      }
    } catch (error) {
      console.warn('保存蓝牙设备到数据库失败:', error)
    }
  }

  // ==================== 连接管理 ====================

  async connect(options = {}) {
    const { forceReconnect = false, silent = false } = options

    try {
      // 已连接且不需要强制重连
      if (this.isConnected && !forceReconnect) {
        return { success: true, deviceName: this.getConnectedDeviceName(), deviceId: this.getConnectedDeviceId() }
      }

      // APP 环境
      if (this.isAppEnvironment()) {
        if (this.isIOS()) {
          return await this.connectNativeBLE(options)
        }
        if (this.isAndroid()) {
          if (window.Capacitor?.Plugins?.ClassicBluetooth) {
            return await this.connectNativeClassic(options)
          }
          return await this.connectNativeBLE(options)
        }
        // 其他平台
        if (window.Capacitor?.Plugins?.ClassicBluetooth) {
          return await this.connectNativeClassic(options)
        }
        if (window.Capacitor?.Plugins?.BluetoothLe) {
          return await this.connectNativeBLE(options)
        }
        return { success: false, error: '蓝牙打印插件未安装，请联系管理员' }
      }

      // Web 环境
      if (!navigator.bluetooth) {
        return { success: false, error: '当前浏览器不支持蓝牙打印，请使用 Chrome 或 Edge' }
      }

      // 自动连接已保存的设备（Chrome 104+）
      if (this.savedDeviceId && !forceReconnect) {
        const autoConnectResult = await this.tryAutoConnect()
        if (autoConnectResult.success) {
          this.resetReconnectAttempts()
          return autoConnectResult
        }
      }

      if (this.savedDeviceName && !silent) {
        return await this.quickConnect()
      }

      if (!silent) {
        return await this.requestDeviceConnection()
      }

      return { success: false, error: '无法自动连接，请手动点击连接' }
    } catch (error) {
      console.error('蓝牙连接失败:', error)
      return { success: false, error: error.message }
    }
  }

  getConnectedDeviceName() {
    if (this.isNativeMode && this.nativeDevice) return this.nativeDevice.name
    if (this.device) return this.device.name
    return 'Unknown'
  }

  getConnectedDeviceId() {
    if (this.isNativeMode && this.nativeDevice) return this.nativeDevice.deviceId
    if (this.device) return this.device.id
    return null
  }

  disconnect() {
    if (this.isNativeMode) {
      const ClassicBluetooth = window.Capacitor?.Plugins?.ClassicBluetooth
      if (ClassicBluetooth && this.isAndroid()) {
        ClassicBluetooth.disconnect()
      }
      const BluetoothLe = window.Capacitor?.Plugins?.BluetoothLe
      if (BluetoothLe && this.nativeDevice?.deviceId) {
        BluetoothLe.disconnect({ deviceId: this.nativeDevice.deviceId })
      }
      this.bleWriteCharacteristic = null
      this.bleServiceUuid = null
    } else if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect()
    }
    this.isConnected = false
  }

  // ==================== 自动重连 ====================

  resetReconnectAttempts() {
    this.reconnectAttempts = 0
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  onDisconnect() {
    this.isConnected = false
    this.characteristic = null
    if (this.autoReconnectEnabled && !this.isReconnecting) {
      this.scheduleReconnect()
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return
    
    this.isReconnecting = true
    this.reconnectAttempts++
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000)
    
    this.reconnectTimer = setTimeout(async () => {
      const result = await this.connect({ forceReconnect: true, silent: true })
      this.isReconnecting = false
      if (result.success) {
        this.resetReconnectAttempts()
      } else {
        this.scheduleReconnect()
      }
    }, delay)
  }

  // ==================== 原生经典蓝牙（Android SPP）====================

  async connectNativeClassic(options = {}) {
    try {
      const ClassicBluetooth = window.Capacitor?.Plugins?.ClassicBluetooth
      if (!ClassicBluetooth) {
        return { success: false, error: '经典蓝牙插件未安装' }
      }
      
      const supportResult = await ClassicBluetooth.checkSupport()
      if (!supportResult.supported) throw new Error('设备不支持蓝牙')
      if (!supportResult.enabled) throw new Error('请开启蓝牙')
      
      if (this.isConnected && this.nativeDevice) {
        return { success: true, deviceName: this.nativeDevice.name, deviceId: this.nativeDevice.deviceId }
      }

      const pairedResult = await ClassicBluetooth.getPairedDevices()
      const pairedDevices = pairedResult.devices || []
      
      if (pairedDevices.length === 0) {
        throw new Error('未找到已配对的蓝牙设备，请先在系统蓝牙设置中配对打印机')
      }
      
      // 过滤打印机设备
      const printerKeywords = ['printer', 'print', 'pos', 'esc', 'receipt', 'thermal', 'bt', 'mpp']
      const printerDevices = pairedDevices.filter(d => {
        const name = (d.name || '').toLowerCase()
        return printerKeywords.some(kw => name.includes(kw))
      })
      
      // 自动连接已保存的设备
      if (this.savedDeviceId && !options.forceReconnect) {
        const savedIsPrinter = printerDevices.some(d => d.address === this.savedDeviceId)
        if (savedIsPrinter) {
          try {
            const result = await ClassicBluetooth.connect({ address: this.savedDeviceId })
            this.nativeDevice = { deviceId: this.savedDeviceId, name: result.name || this.savedDeviceName }
            this.isNativeMode = true
            this.isConnected = true
            return { success: true, deviceName: result.name || this.savedDeviceName, deviceId: this.savedDeviceId }
          } catch (err) {
            // 自动连接失败，继续扫描
          }
        }
      }
      
      const candidates = printerDevices.length > 0 ? printerDevices : pairedDevices
      const selectedDevice = this.savedDeviceName 
        ? candidates.find(d => d.name === this.savedDeviceName) || candidates[0]
        : candidates[0]
      
      const result = await ClassicBluetooth.connect({ address: selectedDevice.address })
      this.nativeDevice = { deviceId: selectedDevice.address, name: selectedDevice.name }
      this.isNativeMode = true
      this.isConnected = true
      
      await this.saveDeviceToDatabase(selectedDevice.address, selectedDevice.name)
      return { success: true, deviceName: result.name || selectedDevice.name, deviceId: selectedDevice.address }
      
    } catch (error) {
      console.error('经典蓝牙连接失败:', error)
      return { success: false, error: error.message }
    }
  }

  // ==================== 原生 BLE 蓝牙（iOS / Android）====================

  async connectNativeBLE(options = {}) {
    try {
      const BluetoothLe = window.Capacitor?.Plugins?.BluetoothLe
      if (!BluetoothLe) {
        return { success: false, error: 'BLE 蓝牙插件未安装' }
      }
      
      // 初始化（iOS XPC 可能卡死，加超时）
      try {
        const initPromise = BluetoothLe.initialize()
        const initTimeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('蓝牙初始化超时，请重启APP或开关蓝牙后重试')), 5000)
        )
        await Promise.race([initPromise, initTimeout])
      } catch (initError) {
        console.error('BLE 初始化失败:', initError)
        return { success: false, error: '蓝牙初始化失败，请关闭再打开蓝牙后重试' }
      }
      
      // 检查蓝牙状态
      const enabled = await BluetoothLe.isEnabled()
      const isBleEnabled = enabled.value === true || enabled.enabled === true
      if (!isBleEnabled) {
        throw new Error('请开启蓝牙')
      }
      
      // 已连接直接返回
      if (this.isConnected && this.nativeDevice) {
        return { success: true, deviceName: this.nativeDevice.name, deviceId: this.nativeDevice.deviceId }
      }

      // 尝试自动连接已保存的设备
      if (this.savedDeviceId && !options.forceReconnect) {
        try {
          await BluetoothLe.connect({ deviceId: this.savedDeviceId })
          this.nativeDevice = { deviceId: this.savedDeviceId, name: this.savedDeviceName }
          this.isNativeMode = true
          this.isConnected = true
          return { success: true, deviceName: this.savedDeviceName, deviceId: this.savedDeviceId }
        } catch (err) {
          // 自动连接失败，继续扫描
        }
      }
      
      // 请求用户选择设备
      let selectedDevice
      if (typeof BluetoothLe.requestDevice === 'function') {
        selectedDevice = await BluetoothLe.requestDevice({
          acceptAllDevices: true,
          optionalServices: this.SERVICE_UUIDS
        })
      } else {
        const devicesResult = await BluetoothLe.getDevices()
        const devices = devicesResult?.devices || []
        if (devices.length === 0) {
          throw new Error('未找到蓝牙设备。请确保打印机已开启且在附近')
        }
        const printerKeywords = ['printer', 'print', 'pos', 'esc', 'receipt', 'thermal', 'bt', 'mpp', 'izm', 'id3']
        const printerDevices = devices.filter(d => {
          const name = (d.name || '').toLowerCase()
          return printerKeywords.some(kw => name.includes(kw))
        })
        const candidates = printerDevices.length > 0 ? printerDevices : devices
        selectedDevice = this.savedDeviceName 
          ? candidates.find(d => d.name === this.savedDeviceName) || candidates[0]
          : candidates[0]
      }
      
      if (!selectedDevice) {
        throw new Error('未选择设备')
      }
      
      const deviceId = selectedDevice.deviceId || selectedDevice.address
      await BluetoothLe.connect({ deviceId })
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      this.nativeDevice = { deviceId, name: selectedDevice.name }
      this.isNativeMode = true
      this.isConnected = true
      
      await this.saveDeviceToDatabase(deviceId, selectedDevice.name)
      return { success: true, deviceName: selectedDevice.name, deviceId }
      
    } catch (error) {
      console.error('BLE 连接失败:', error)
      return { success: false, error: error?.message || '未知错误' }
    }
  }

  // ==================== Web Bluetooth 连接 ====================

  async tryAutoConnect() {
    try {
      if (navigator.bluetooth.getDevices) {
        const devices = await navigator.bluetooth.getDevices()
        const savedDevice = devices.find(d => d.id === this.savedDeviceId)
        if (savedDevice) {
          return await this.connectToDevice(savedDevice)
        }
      }
      return { success: false }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async connectToDevice(device) {
    this.device = device

    this.device.addEventListener('gattserverdisconnected', () => {
      this.isConnected = false
    })

    this.server = await this.device.gatt.connect()
    if (!this.server.connected) {
      this.server = await this.device.gatt.connect()
    }
    
    const services = await this.server.getPrimaryServices()
    if (!services?.length) throw new Error('未找到蓝牙服务')
    this.service = services[0]
    
    const characteristics = await this.service.getCharacteristics()
    for (const char of characteristics) {
      if (char.properties.write || char.properties.writeWithoutResponse) {
        this.characteristic = char
        break
      }
    }

    if (!this.characteristic) throw new Error('未找到可写入的特征值')

    this.isConnected = true
    return { success: true, deviceName: this.device.name, deviceId: this.device.id }
  }

  async quickConnect() {
    try {
      this.device = await navigator.bluetooth.requestDevice({
        filters: [{ name: this.savedDeviceName }],
        optionalServices: this.SERVICE_UUIDS
      })
      return await this.connectToDevice(this.device)
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  async requestDeviceConnection() {
    try {
      this.device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: this.SERVICE_UUIDS
      })

      this.device.addEventListener('gattserverdisconnected', () => {
        this.isConnected = false
      })

      this.server = await this.device.gatt.connect()
      const services = await this.server.getPrimaryServices()
      if (!services?.length) throw new Error('未找到蓝牙服务')
      this.service = services[0]
      
      const characteristics = await this.service.getCharacteristics()
      for (const char of characteristics) {
        if (char.properties.write || char.properties.writeWithoutResponse) {
          this.characteristic = char
          break
        }
      }

      if (!this.characteristic) throw new Error('未找到可写入的特征值')

      this.isConnected = true
      await this.saveDeviceToDatabase(this.device.id, this.device.name)
      
      return { success: true, deviceName: this.device.name, deviceId: this.device.id }
    } catch (error) {
      console.error('设备连接失败:', error)
      throw error
    }
  }

  // ==================== 连接状态检查 ====================

  async ensureConnected() {
    // 原生模式
    if (this.isNativeMode && this.nativeDevice?.deviceId) {
      // Android 经典蓝牙
      if (this.isAndroid() && window.Capacitor?.Plugins?.ClassicBluetooth) {
        try {
          const result = await window.Capacitor.Plugins.ClassicBluetooth.isConnected()
          if (result.connected) return true
        } catch (e) { /* ignore */ }
        return false
      }
      
      // BLE
      if (window.Capacitor?.Plugins?.BluetoothLe) {
        try {
          const BluetoothLe = window.Capacitor.Plugins.BluetoothLe
          const services = await BluetoothLe.getServices({ deviceId: this.nativeDevice.deviceId })
          if (services?.services?.length > 0) {
            this.isConnected = true
            return true
          }
        } catch (e) { /* ignore */ }
        
        // 尝试重连
        try {
          await window.Capacitor.Plugins.BluetoothLe.connect({ deviceId: this.nativeDevice.deviceId })
          await new Promise(resolve => setTimeout(resolve, 1000))
          this.isConnected = true
          return true
        } catch (e) { /* ignore */ }
        return false
      }
    }
    
    // Web Bluetooth
    if (this.characteristic) return true
    
    if (this.device?.gatt) {
      try {
        if (!this.device.gatt.connected) {
          this.server = await this.device.gatt.connect()
          const services = await this.server.getPrimaryServices()
          if (services?.length > 0) {
            this.service = services[0]
            const characteristics = await this.service.getCharacteristics()
            for (const char of characteristics) {
              if (char.properties.write || char.properties.writeWithoutResponse) {
                this.characteristic = char
                this.isConnected = true
                return true
              }
            }
          }
        } else {
          this.isConnected = true
          return true
        }
      } catch (error) { /* ignore */ }
    }
    
    return false
  }

  // ==================== 数据写入 ====================

  /**
   * BLE 分包发送（hex 字符串格式，IZM 打印机每次最多 100 字节）
   */
  async bleWritePacked(dataArray) {
    const BluetoothLe = window.Capacitor.Plugins.BluetoothLe
    const PACK_SIZE = this.BLE_PACK_SIZE
    
    for (let i = 0; i < dataArray.length; i += PACK_SIZE) {
      const end = Math.min(i + PACK_SIZE, dataArray.length)
      const pack = dataArray.slice(i, end)
      
      // 转为 hex 字符串（@capacitor-community/bluetooth-le 插件要求）
      const hexStr = pack.map(b => b.toString(16).padStart(2, '0')).join('')
      
      await BluetoothLe.write({
        deviceId: this.nativeDevice.deviceId,
        service: this.bleServiceUuid,
        characteristic: this.bleWriteCharacteristic,
        value: hexStr
      })
      
      // 每包之间加延迟
      if (i + PACK_SIZE < dataArray.length) {
        await new Promise(resolve => setTimeout(resolve, 20))
      }
    }
  }

  async write(data) {
    const connected = await this.ensureConnected()
    if (!connected) throw new Error('打印机未连接，请重新连接')
    
    if (this.isNativeMode) {
      const ClassicBluetooth = window.Capacitor?.Plugins?.ClassicBluetooth
      const BluetoothLe = window.Capacitor?.Plugins?.BluetoothLe
      
      if (ClassicBluetooth && this.isAndroid()) {
        await ClassicBluetooth.write({ data })
      } else if (BluetoothLe) {
        const buffer = this.encodeData(data)
        const view = new Uint8Array(buffer)
        
        if (!this.bleWriteCharacteristic) {
          await this.discoverBLECharacteristics()
        }
        if (!this.bleWriteCharacteristic || !this.bleServiceUuid) {
          throw new Error('未找到可写入的蓝牙特征')
        }
        
        await this.bleWritePacked(Array.from(view))
      } else {
        throw new Error('蓝牙插件未加载')
      }
    } else {
      if (!this.characteristic) throw new Error('打印机未连接，请重新连接')
      const buffer = this.encodeData(data)
      await this.characteristic.writeValue(buffer)
    }
    
    await new Promise(resolve => setTimeout(resolve, 50))
  }

  async sendEscPos(commands) {
    const connected = await this.ensureConnected()
    if (!connected) throw new Error('打印机未连接')
    
    if (this.isNativeMode) {
      if (this.isAndroid() && window.Capacitor?.Plugins?.ClassicBluetooth) {
        await window.Capacitor.Plugins.ClassicBluetooth.writeRaw({ data: commands })
      } else if (window.Capacitor?.Plugins?.BluetoothLe) {
        if (!this.bleWriteCharacteristic) {
          await this.discoverBLECharacteristics()
        }
        if (!this.bleWriteCharacteristic) {
          throw new Error('未找到可写入的蓝牙特征')
        }
        await this.bleWritePacked(commands)
      } else {
        throw new Error('蓝牙插件未加载')
      }
    } else {
      if (!this.characteristic) throw new Error('打印机未连接')
      const buffer = new ArrayBuffer(commands.length)
      const view = new Uint8Array(buffer)
      commands.forEach((cmd, i) => view[i] = cmd)
      await this.characteristic.writeValue(buffer)
    }
    
    await new Promise(resolve => setTimeout(resolve, 50))
  }

  async writeBLEBuffer(buffer) {
    if (this.isNativeMode) {
      if (this.isAndroid() && window.Capacitor?.Plugins?.ClassicBluetooth) {
        const view = new Uint8Array(buffer)
        await window.Capacitor.Plugins.ClassicBluetooth.writeRaw({ data: Array.from(view) })
      } else if (window.Capacitor?.Plugins?.BluetoothLe) {
        if (!this.bleWriteCharacteristic) {
          await this.discoverBLECharacteristics()
        }
        if (!this.bleWriteCharacteristic) {
          throw new Error('未找到可写入的蓝牙特征')
        }
        const view = new Uint8Array(buffer)
        await this.bleWritePacked(Array.from(view))
      } else {
        throw new Error('蓝牙插件未加载')
      }
    } else {
      await this.characteristic.writeValue(buffer)
    }
  }

  // ==================== BLE 服务发现 ====================

  async discoverBLECharacteristics() {
    const BluetoothLe = window.Capacitor?.Plugins?.BluetoothLe
    if (!BluetoothLe || !this.nativeDevice?.deviceId) {
      throw new Error('BLE 插件未加载或设备未连接')
    }
    
    const servicesResult = await BluetoothLe.getServices({ deviceId: this.nativeDevice.deviceId })
    const services = servicesResult?.services || []
    
    // 查找打印机服务
    for (const service of services) {
      const serviceUuid = service.uuid.toLowerCase()
      if (this.SERVICE_UUIDS.some(uuid => serviceUuid.includes(uuid.toLowerCase()))) {
        const characteristics = service.characteristics || []
        for (const char of characteristics) {
          const props = char.properties || {}
          if (props.write || props.writeWithoutResponse) {
            this.bleWriteCharacteristic = char.uuid
            this.bleServiceUuid = service.uuid
            return
          }
        }
      }
    }
    
    // 没找到打印机服务，尝试所有服务的写入特征
    for (const service of services) {
      try {
        const charsResult = await BluetoothLe.getCharacteristics({
          deviceId: this.nativeDevice.deviceId,
          service: service.uuid
        })
        const characteristics = charsResult?.characteristics || []
        for (const char of characteristics) {
          const props = char.properties || {}
          if (props.write || props.writeWithoutResponse) {
            this.bleWriteCharacteristic = char.uuid
            this.bleServiceUuid = service.uuid
            return
          }
        }
      } catch (e) { /* 某些服务可能无法访问，跳过 */ }
    }
    
    throw new Error('未找到可写入的蓝牙特征')
  }

  // ==================== 编码 ====================

  encodeData(text) {
    try {
      const gbkBytes = GBK.encode(text)
      const buffer = new ArrayBuffer(gbkBytes.length)
      const view = new Uint8Array(buffer)
      gbkBytes.forEach((byte, index) => { view[index] = byte })
      return buffer
    } catch (error) {
      console.error('GBK 编码失败:', error)
      const encoder = new TextEncoder()
      return encoder.encode(text).buffer
    }
  }

  // ==================== ESC/POS 打印命令 ====================

  async init() {
    await this.sendEscPos([0x1B, 0x40])
    await new Promise(resolve => setTimeout(resolve, 200))
    await this.sendEscPos([0x1B, 0x74, 0x12])
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  async printText(text, options = {}) {
    const { bold = false, doubleWidth = false, doubleHeight = false, align = 'left', underline = false } = options

    const alignMap = { left: 0, center: 1, right: 2 }
    await this.sendEscPos([0x1B, 0x61, alignMap[align] || 0])

    let style = 0
    if (bold) style |= 0x08
    if (underline) style |= 0x80
    if (style) await this.sendEscPos([0x1B, 0x45, style])

    if (doubleWidth || doubleHeight) {
      const size = (doubleHeight ? 0x10 : 0) | (doubleWidth ? 0x01 : 0)
      await this.sendEscPos([0x1D, 0x21, size])
    }

    await this.write(text + '\r\n')

    if (style || doubleWidth || doubleHeight) {
      await this.init()
    }
  }

  // ==================== 配送单打印 ====================

  async printDeliveryTask(task) {
    try {
      await this.init()

      const LINE_WIDTH = 48
      const SEPARATOR = '-'.repeat(LINE_WIDTH)
      const DOUBLE_SEPARATOR = '='.repeat(LINE_WIDTH)

      await this.sendEscPos([0x1B, 0x61, 0x01])
      await this.sendEscPos([0x1D, 0x21, 0x11])
      await this.write('Delivery Task\r\n')
      await this.sendEscPos([0x1D, 0x21, 0x00])
      await this.write('\r\n')
      
      await this.write(`${DOUBLE_SEPARATOR}\r\n`)
      
      await this.sendEscPos([0x1B, 0x61, 0x00])
      await this.write(`Task No: ${task.taskNo || 'N/A'}\r\n`)
      const createTime = task.createdAt ? new Date(task.createdAt).toLocaleString('en-SG') : ''
      await this.write(`Order Time: ${createTime}\r\n`)
      await this.write(`\r\n`)
      
      await this.sendEscPos([0x1B, 0x45, 0x01])
      await this.write('📍 Pickup Info\r\n')
      await this.sendEscPos([0x1B, 0x45, 0x00])
      if (task.pickupAddress) await this.write(`Address: ${task.pickupAddress}\r\n`)
      if (task.pickupContact) await this.write(`Contact: ${task.pickupContact}\r\n`)
      if (task.pickupPhone) await this.write(`Phone: ${task.pickupPhone}\r\n`)
      await this.write(`\r\n`)
      
      await this.sendEscPos([0x1B, 0x45, 0x01])
      await this.write('🏠 Delivery Info\r\n')
      await this.sendEscPos([0x1B, 0x45, 0x00])
      if (task.deliveryAddress) await this.write(`Address: ${task.deliveryAddress}\r\n`)
      if (task.deliveryContact) await this.write(`Contact: ${task.deliveryContact}\r\n`)
      if (task.deliveryPhone) await this.write(`Phone: ${task.deliveryPhone}\r\n`)
      await this.write(`\r\n`)
      
      if (task.items?.length > 0) {
        await this.sendEscPos([0x1B, 0x45, 0x01])
        await this.write('📦 Items\r\n')
        await this.sendEscPos([0x1B, 0x45, 0x00])
        
        await this.write(`${SEPARATOR}\r\n`)
        await this.write('Item                    Qty       Price\r\n')
        await this.write(`${SEPARATOR}\r\n`)
        
        let totalAmount = 0
        for (const item of task.items) {
          const name = (item.productName || '').substring(0, 20).padEnd(20)
          const qty = String(item.quantity || 0).padEnd(10)
          const price = `$${item.price || 0}`
          await this.write(`${name}${qty}${price}\r\n`)
          totalAmount += (item.quantity || 0) * (item.price || 0)
        }
        await this.write(`${SEPARATOR}\r\n`)
        await this.write(`Total: $${totalAmount.toFixed(2)}\r\n`)
        await this.write(`\r\n`)
      }
      
      if (task.remark) {
        await this.sendEscPos([0x1B, 0x45, 0x01])
        await this.write('📝 Remark\r\n')
        await this.sendEscPos([0x1B, 0x45, 0x00])
        await this.write(`${task.remark}\r\n`)
        await this.write(`\r\n`)
      }
      
      await this.write(`${DOUBLE_SEPARATOR}\r\n`)
      
      await this.sendEscPos([0x1B, 0x61, 0x01])
      await this.write(`Driver: ${task.driverName || '-'}\r\n`)
      const printTime = new Date().toLocaleString('en-SG')
      await this.write(`Print Time: ${printTime}\r\n`)
      
      await this.write('\r\n\r\n\r\n')
      await this.sendEscPos([0x1D, 0x56, 0x41, 0x03])

      return { success: true }
    } catch (error) {
      console.error('打印失败:', error)
      return { success: false, error: error.message }
    }
  }

  // ==================== 订单小票打印（新加坡格式）====================

  /**
   * 打印 ESC/POS 条形码（Code128）
   */
  async printBarcode(data) {
    if (!data) return
    // 设置条形码高度
    await this.sendEscPos([0x1D, 0x68, 0x50]) // 80 dots
    // 设置条形码宽度
    await this.sendEscPos([0x1D, 0x77, 0x02]) // 2x
    // 设置 HRI 字符位置（下方）
    await this.sendEscPos([0x1D, 0x48, 0x02])
    // Code128 条形码
    const bytes = []
    bytes.push(0x1B, 0x7B, 0x42) // ESC { B (Code128)
    for (let i = 0; i < data.length; i++) {
      bytes.push(data.charCodeAt(i))
    }
    await this.sendEscPos(bytes)
    await this.write('\r\n')
  }

  async printSingaporeOrder(order, customer, receivableData = null) {
    try {
      await this.init()

      const LINE_WIDTH = 48
      const SEPARATOR = '-'.repeat(LINE_WIDTH)
      const DOUBLE_SEPARATOR = '='.repeat(LINE_WIDTH)

      // ========== 商户信息 ==========
      const merchantName = customer?.companyName || customer?.customerName || order.customerName || 'WHOLESALE SYSTEM'
      const merchantAddress = customer?.companyAddress || order.deliveryAddress || ''
      const merchantPhone = customer?.phone || customer?.contactPerson || ''
      
      await this.sendEscPos([0x1B, 0x61, 0x01]) // 居中
      await this.sendEscPos([0x1D, 0x21, 0x11]) // 双倍大小
      await this.write(`${merchantName}\r\n`)
      await this.sendEscPos([0x1D, 0x21, 0x00]) // 恢复正常
      await this.sendEscPos([0x1B, 0x61, 0x00]) // 左对齐
      
      if (merchantAddress) await this.write(`${merchantAddress}\r\n`)
      if (merchantPhone) await this.write(`Tel: ${merchantPhone}\r\n`)
      
      // GST No
      if (customer?.gstNo) {
        await this.write(`GST No: ${customer.gstNo}\r\n`)
      }
      
      await this.write(`${DOUBLE_SEPARATOR}\r\n`)
      
      // ========== 交易信息 ==========
      await this.write(`Terminal: ${order.sourceChannel || 'WEB'}\r\n`)
      const orderDate = order.createdAt ? new Date(order.createdAt).toLocaleString('en-SG') : ''
      await this.write(`${orderDate}\r\n`)
      await this.write(`Invoice No: ${order.invoiceNo || order.orderNo || 'N/A'}\r\n`)
      
      if (order.invoiceUuid) {
        const uuid = order.invoiceUuid
        await this.write(`UUID: ${uuid.substring(0, 19)}\r\n`)
        await this.write(`      ${uuid.substring(19)}\r\n`)
      }
      
      await this.write(`Order: ${order.orderNo || 'N/A'}\r\n`)
      await this.write(`Cashier: ${order.createdBy || '001'}\r\n`)
      await this.write(`${SEPARATOR}\r\n`)
      
      // ========== 商品明细 ==========
      await this.sendEscPos([0x1B, 0x45, 0x01]) // 加粗
      await this.write('No   Item            Qty    Amt\r\n')
      await this.sendEscPos([0x1B, 0x45, 0x00]) // 取消加粗
      await this.write(`${SEPARATOR}\r\n`)
      
      let totalItemTax = 0
      if (order.items?.length > 0) {
        for (const [index, item] of order.items.entries()) {
          const name = (item.productName || item.name || '').substring(0, 12)
          const qty = String(item.quantity || 0).padStart(5)
          const amount = item.subtotal || ((item.quantity || 0) * (item.unitPrice || item.price || 0))
          
          await this.write(`${String(index + 1).padStart(2)} ${name.padEnd(12)} ${qty} $${parseFloat(amount).toFixed(2).padStart(7)}\r\n`)
          
          // 逐项 GST 显示
          if (item.taxRate && item.taxRate > 0) {
            const taxPercent = Math.round(item.taxRate * 100)
            const itemTax = item.taxAmount || 0
            totalItemTax += itemTax
            await this.write(`   GST ${taxPercent}%: $${parseFloat(itemTax).toFixed(2)}\r\n`)
          } else if (item.taxRate !== null && item.taxRate !== undefined && item.taxRate === 0) {
            await this.write(`   (Tax Free)\r\n`)
          }
          
          if (item.sku || item.productSku) {
            await this.write(`    SKU: ${item.sku || item.productSku}\r\n`)
          }
        }
      }
      await this.write(`${SEPARATOR}\r\n`)
      
      // ========== 金额汇总 ==========
      const total = order.totalAmount || 0
      const subtotal = order.subtotal || 0
      const gstAmount = order.gstAmount || totalItemTax
      
      await this.write(`Subtotal:                 $${parseFloat(subtotal).toFixed(2).padStart(7)}\r\n`)
      
      // GST 明细
      if (gstAmount > 0) {
        const gstRate = order.gstRate ? Math.round(order.gstRate * 100) : 9
        await this.write(`GST (${gstRate}%):              $${parseFloat(gstAmount).toFixed(2).padStart(7)}\r\n`)
      } else {
        await this.write(`Tax Free\r\n`)
      }
      
      await this.write(`${SEPARATOR}\r\n`)
      await this.write(`Total:                    $${parseFloat(total).toFixed(2).padStart(7)}\r\n`)
      await this.write(`Items: ${order.items?.length || 0}\r\n`)
      await this.write(`Pay: ${order.paymentMethod || 'COD'}\r\n`)
      
      // ========== 银行信息 ==========
      if (customer?.bankName) {
        await this.write(`${SEPARATOR}\r\n`)
        await this.write(`Bank: ${customer.bankName}\r\n`)
        if (customer.bankAccountName) {
          await this.write(`Acc:  ${customer.bankAccountName}\r\n`)
        }
        if (customer.bankAccountNumber) {
          await this.write(`No:   ${customer.bankAccountNumber}\r\n`)
        }
      }
      
      // ========== PayNow 二维码 ==========
      if (customer?.paynowQr) {
        await this.write(`${SEPARATOR}\r\n`)
        // ESC/POS 不支持直接打印二维码，打印 PayNow URL 文本
        await this.write(`PayNow: ${customer.paynowQr}\r\n`)
      }
      
      // ========== 条形码 + 订单二维码 ==========
      await this.write(`${SEPARATOR}\r\n`)
      if (order.orderNo) {
        const barcodeContent = order.orderNo.length <= 25 ? order.orderNo : order.orderNo.substring(order.orderNo.length - 25)
        await this.printBarcode(barcodeContent)
      }
      
      // ========== 备注 ==========
      if (order.remark) {
        await this.write(`${SEPARATOR}\r\n`)
        await this.sendEscPos([0x1B, 0x45, 0x01]) // 加粗
        await this.write(`Note\r\n`)
        await this.sendEscPos([0x1B, 0x45, 0x00]) // 取消加粗
        await this.write(`${order.remark.substring(0, 60)}\r\n`)
      }
      
      await this.write(`${SEPARATOR}\r\n`)
      
      // ========== 应收款汇总 ==========
      if (receivableData?.totalUnpaid > 0) {
        await this.sendEscPos([0x1B, 0x45, 0x01]) // 加粗
        await this.write(`Outstanding Balance: ${receivableData.customerName || ''}\r\n`)
        await this.sendEscPos([0x1B, 0x45, 0x00]) // 取消加粗
        await this.write(`${SEPARATOR}\r\n`)
        await this.write('Order No      Date          Unpaid\r\n')
        await this.write(`${SEPARATOR}\r\n`)
        if (receivableData.orders?.length > 0) {
          for (const o of receivableData.orders) {
            const orderNo = (o.orderNo || '').padEnd(14)
            const date = (o.orderDate || '').padEnd(12)
            const unpaid = `$${parseFloat(o.unpaidAmount || 0).toFixed(2)}`
            await this.write(`${orderNo}${date}${unpaid}\r\n`)
          }
        }
        await this.write(`${SEPARATOR}\r\n`)
        await this.sendEscPos([0x1B, 0x45, 0x01]) // 加粗
        await this.write(`Total Unpaid:  $${parseFloat(receivableData.totalUnpaid || 0).toFixed(2)}\r\n`)
        await this.write(`(${receivableData.unpaidOrderCount || 0} orders)\r\n`)
        await this.sendEscPos([0x1B, 0x45, 0x00]) // 取消加粗
        await this.write(`${SEPARATOR}\r\n`)
      }

      // ========== 底部 ==========
      await this.write('Thank You / 谢谢惠顾\r\n')
      await this.write('Exchange Within 3 Days\r\n')
      
      await this.write('\r\n\r\n\r\n')
      await this.sendEscPos([0x1D, 0x56, 0x41, 0x03]) // 切纸

      return { success: true }
    } catch (error) {
      console.error('打印失败:', error)
      return { success: false, error: error.message }
    }
  }
}

let printerInstance = null

export function getPrinter() {
  if (!printerInstance) {
    printerInstance = new BluetoothPrinter()
  }
  return printerInstance
}

export function isBluetoothSupported() {
  if (typeof window !== 'undefined' && window.Capacitor) {
    return !!(window.Capacitor.Plugins?.ClassicBluetooth)
  }
  return !!(navigator.bluetooth && navigator.bluetooth.requestDevice)
}

export default getPrinter
